import { ReadonlyVec2 } from 'gl-matrix';
import { atom } from 'recoil';
import { DoseId } from '~/domain/changes/ids';
import {
    DEFAULT_MAX_DOSE_RANGE,
    DEFAULT_MIN_DOSE_RANGE,
} from '~/engine/data/dose/DoseDistribution';

/**
 * Transient per-dose *display* settings — the colorwash window, the display
 * scale, and the display normalization.
 *
 * These are visualization parameters, not clinical domain state: they change
 * how the dose is shown (which colours, what magnitude the labels report,
 * what "100 %" references) but never the dose grid, the DVH values, or any
 * exported DICOM. They used to live on the `DoseMatrix` entity purely to get
 * per-dose persistence across dose switching; that pulled view state into the
 * tracked domain aggregate. They now live here, in the UI layer, keyed by the
 * per-session {@link DoseId}, which is exactly the lifetime the persistence
 * needs (survive switching within a session; nothing is stored in DICOM).
 */
export interface DoseDisplaySettings {
    /**
     * Colorwash window [min, max] normalized to 0..1 of max dose.
     *
     * Typed `ReadonlyVec2` and stored as a plain frozen tuple, never a
     * `Float32Array`: entries are shared by reference with every reader, and
     * the deleted `DoseMatrix.selectedRange` getter used to clone on every
     * read for exactly this reason. A readonly tuple makes `range[0] = x` a
     * compile error, and every stored tuple — the default here and each
     * committed entry, which `validatePatch` freezes on write — throws on an
     * in-place write. Neither guard can be left to Recoil: its dev-mode deep
     * freeze skips typed arrays and does not reach values held inside a Map.
     * An unguarded write would silently change the committed window for
     * every reader, with no store update and no re-render.
     */
    readonly selectedRange: ReadonlyVec2;
    /** Dimensionless display-time multiplier applied to reported dose values. */
    readonly doseScale: number;
    /**
     * 100 %-normalization dose in Gy, or `null` to normalize against the
     * dose's own maximum (resolved by the reader, which knows `doseMaxGy`).
     */
    readonly normalizationValueGy: number | null;
}

/**
 * Shared by every dose that has no stored settings, so it must be a stable
 * reference (consumers memoize on its identity) *and* unwritable.
 */
export const DEFAULT_DOSE_DISPLAY_SETTINGS: DoseDisplaySettings = Object.freeze(
    {
        selectedRange: Object.freeze([
            DEFAULT_MIN_DOSE_RANGE,
            DEFAULT_MAX_DOSE_RANGE,
        ]) as ReadonlyVec2,
        doseScale: 1.0,
        normalizationValueGy: null,
    }
);

/**
 * Committed display settings per dose. Updated immutably (replace the map)
 * through {@link useDoseDisplaySettings}, never mutated in place.
 *
 * NB: this holds the *committed* colorwash range. The live in-flight value
 * during a slider drag stays in {@link selectedDoseRangeState} so the drag
 * updates the engine per frame without rewriting this map every frame.
 *
 * Entries are dropped when their dose is deleted and the whole map is reset
 * when a patient is unloaded — see `useDeleteDataset` and `useUnloadPatient`.
 * A `DoseId` is a per-session uuid, so a surviving entry could never be read
 * back; it would only accumulate one deleted patient's prescription after
 * another for the lifetime of the app.
 */
export const doseDisplaySettingsState = atom<
    ReadonlyMap<DoseId, DoseDisplaySettings>
>({
    key: 'doseDisplaySettings',
    default: new Map<DoseId, DoseDisplaySettings>(),
});
